import {
    getWorkspaceSettingsForPlan,
    STANDARD_PRICE_PER_CREDIT_USD
} from "@functions/workspacePlans";
import {
    getIconForWorkspacePlan,
    getTaglineForWorkspacePlan
} from "../scripts/routes/workspace/utils";
import { PLANS } from "@functions/plans/information";
import { Plan } from "@functions/plans/types";
import { getPricingTablePlans } from "@functions/plans/utilities";

const publicPlanLimits = PLANS[Plan.Public];
const basicPlanLimits = PLANS[Plan.UbpBasic];
const corePlanLimits = PLANS[Plan.UbpCore];
const growthPlanLimits = PLANS[Plan.UbpGrowth];

export const frequencies = [
    { interval: "monthly", label: "Monthly", labelWithoutDiscount: "Monthly" },
    { interval: "annually", label: "Annual", labelWithoutDiscount: "Annual", discountRate: "25%" }
];

/**
 * Get plan pricing with Stripe price IDs
 * @param {object} projectSelector - The projectSelector object (defaults to window.project for backwards compatibility)
 * @returns {object} Plan pricing configuration
 */
export function getPlanPricing(
    projectSelector = typeof window != "undefined" ? window?.project : null
) {
    const stripe = projectSelector?.stripe?.();

    return {
        "public": {
            monthly: {
                planType: "public",
                interval: "monthly",
                label: "Monthly",
                priceSuffix: "No credit card needed",
                price: PLANS[Plan.Public].price.monthly
            },
            annually: {
                planType: "public",
                interval: "annually",
                label: "Annually",
                priceSuffix: "No credit card needed",
                price: PLANS[Plan.Public].price.annual
            }
        },
        "basic": {
            monthly: {
                planType: "basic",
                interval: "monthly",
                label: "Monthly",
                priceSuffix: "per month, billed monthly",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpBasic].price.monthly,
                totalPrice: `$${PLANS[Plan.UbpBasic].price.monthly}`,
                priceId: stripe?.basic_plan?.priceId || null,
                chip: `Go annual to save $${Number(PLANS[Plan.UbpBasic].price.monthly) - Number(PLANS[Plan.UbpBasic].price.annual)}/mo`
            },
            annually: {
                planType: "basic",
                interval: "annually",
                label: "Annually",
                priceSuffix: "per month, billed annually",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpBasic].price.annual,
                totalPrice: `$${Number(PLANS[Plan.UbpBasic].price.annual) * 12}`,
                discountRate: "25%",
                priceId: stripe?.basic_plan?.annualPriceId || null,
                chip: `Saving $${Number(PLANS[Plan.UbpBasic].price.monthly) - Number(PLANS[Plan.UbpBasic].price.annual)}/mo`
            }
        },
        "core": {
            monthly: {
                planType: "core",
                interval: "monthly",
                label: "Monthly",
                priceSuffix: "per month, billed monthly",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpCore].price.monthly,
                totalPrice: `$${PLANS[Plan.UbpCore].price.monthly}`,
                priceId: stripe?.core_plan?.priceId || null,
                chip: `Go annual to save $${Number(PLANS[Plan.UbpCore].price.monthly) - Number(PLANS[Plan.UbpCore].price.annual)}/mo`
            },
            annually: {
                planType: "core",
                interval: "annually",
                label: "Annually",
                priceSuffix: "per month, billed annually",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpCore].price.annual,
                totalPrice: `$${Number(PLANS[Plan.UbpCore].price.annual) * 12}`,
                discountRate: "20%",
                priceId: stripe?.core_plan?.annualPriceId || null,
                chip: `Saving $${Number(PLANS[Plan.UbpCore].price.monthly) - Number(PLANS[Plan.UbpCore].price.annual)}/mo`
            }
        },
        "growth-ubp": {
            monthly: {
                planType: "growth-ubp",
                interval: "monthly",
                label: "Monthly",
                priceSuffix: "per month, billed monthly",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpGrowth].price.monthly,
                totalPrice: `$${Number(PLANS[Plan.UbpGrowth].price.monthly) * 12}`,
                priceId: stripe?.growth_plan?.priceId || null,
                chip: `Go annual to save $${Number(PLANS[Plan.UbpGrowth].price.monthly) - Number(PLANS[Plan.UbpGrowth].price.annual)}/mo`
            },
            annually: {
                planType: "growth-ubp",
                interval: "annually",
                label: "Annually",
                priceSuffix: "per month, billed annually",
                condensedPriceSuffix: "/mo",
                price: PLANS[Plan.UbpGrowth].price.annual,
                totalPrice: `$${Number(PLANS[Plan.UbpGrowth].price.annual) * 12}`,
                discountRate: "25%",
                priceId: stripe?.growth_plan?.annualPriceId || null,
                chip: `Saving $${Number(PLANS[Plan.UbpGrowth].price.monthly) - Number(PLANS[Plan.UbpGrowth].price.annual)}/mo`
            }
        },
        "enterprise": {
            monthly: {
                planType: "public",
                interval: "monthly",
                label: "Monthly",
                price: "Custom Pricing",
                priceSuffix: "billed annually"
            },
            annually: {
                planType: "public",
                interval: "annually",
                label: "Annually",
                price: "Custom Pricing",
                priceSuffix: "billed annually"
            }
        }
    };
}

// Re-constructing planPricing from the single source of truth
export const planPricing = getPlanPricing();

export function getPlanData(
    currentPlan,
    frequency = null,
    includedPlans = null,
    usage = null,
    projectSelector = typeof window != "undefined" ? window?.project : null
) {
    const workspacePricePerCredit =
        typeof usage?.ubp?.pricePerCredit === "number" ? usage.ubp.pricePerCredit : null;
    const getPlanDefaultPricePerCredit = (planKey) => {
        const planDefaults = getWorkspaceSettingsForPlan(planKey);
        if (typeof planDefaults?.ubp?.pricePerCredit === "number") {
            return planDefaults.ubp.pricePerCredit;
        }
        return STANDARD_PRICE_PER_CREDIT_USD;
    };
    const getPricePerCreditString = (planKey) => {
        const planDefault = getPlanDefaultPricePerCredit(planKey);
        if (
            planKey === currentPlan &&
            typeof workspacePricePerCredit === "number" &&
            workspacePricePerCredit !== planDefault
        ) {
            return `$${workspacePricePerCredit}`;
        }
        return `$${planDefault}`;
    };

    // Helper to get plan info from centralized source
    const getPlan = (plan) => PLANS[plan];

    // Use dynamic pricing if projectSelector is provided
    const planPricing = getPlanPricing(projectSelector);

    let planData = {
        "public": {
            icon: getIconForWorkspacePlan("public"),
            iconColor: "text-tertiary",
            name: getPlan(Plan.Public).meta.title,
            price: getPlan(Plan.Public).price.monthly,
            priceDetails: "No credit card needed.",
            ctaText: currentPlan === "public" ? "Current Plan" : "Downgrade to Public Plan",
            expirationCtaText: "Downgrade to Public",
            ctaType: "secondary",
            description: getTaglineForWorkspacePlan("public"),
            ctaDisabled: true,
            features: {
                dataPrivacy: "Open source to all users",
                monthlyCredits: getPlan(Plan.Public).ubp.includedCredits,
                pricePerCredit: getPricePerCreditString(Plan.Public),
                userSeats: getPlan(Plan.Public).limit.collaborators,
                projects: getPlan(Plan.Public).limit.projects,
                roleBasedAccess: getPlan(Plan.Public).features.rbac,
                customRoles: false,
                sso: false,
                scopedApiKeys: false,
                datasetSizeLimit: `${getPlan(Plan.Public).limit.sources.toLocaleString()} Images`,
                imageAugmentations: `${getPlan(Plan.Public).limit.variantsPerImage}x`,
                videoProcessing: true,
                datasetSearch: true,
                datasetAnalytics: true,
                enhancedPreprocessing: false,
                enhancedAugmentations: false,
                aiAssistedLabeling: true,
                reviewMode: false,
                annotationHistory: false,
                labelingServices: false,
                annotationInsights: false,
                hostedTraining: "Fast Models Only",
                modelWeightsUpload: true,
                downloadWeights: false,
                modelEvaluation: false,
                concurrentTraining: false,
                premiumGpuAccess: "",
                fineTunedModels: true,
                foundationModels: true,
                activeLearning: true,
                buildDeployWorkflows: true,
                workflowVersions: false,
                modelMonitoring: false,
                deviceManagement: false,
                inferenceLicense: true,
                serverlessApi: true,
                dedicatedDeployment: false,
                managedCluster: false,
                inferenceDeploy: true,
                videoStreamMgmt: true,
                inferenceModelLicense: false,
                downloadLicense: "",
                enterpriseNetworking: false,
                offlineDeployment: false,
                kubernetesOperator: false,
                soc2: true,
                communityForum: true,
                emailSupport: false,
                liveChat: false,
                onboardingCall: false,
                documentationAiChat: true,
                dedicatedSupport: false,
                onsiteInstallation: false,
                managedServices: false,
                customContract: false,
                activeDirectoryIntegration: false,
                securityReview: false,
                baa: false,
                sla: false
            },
            highlightedFeatures: [
                { text: "Access to the Roboflow platform:", excludeIcon: true },
                { text: "credits per month", boldText: getPlan(Plan.Public).ubp.includedCredits },
                { text: "Team Members", boldText: getPlan(Plan.Public).limit.collaborators },
                { text: "Community Support", iconType: "support" }
            ]
        },
        "basic": {
            icon: getIconForWorkspacePlan("basic"),
            name: getPlan(Plan.UbpBasic).meta.title,
            recommended: false,
            price: `$${planPricing.basic[frequency?.interval ?? "monthly"].price}`,
            priceDetails: planPricing.basic[frequency?.interval ?? "monthly"].priceSuffix,
            condensedPriceDetails:
                planPricing.basic[frequency?.interval ?? "monthly"].condensedPriceSuffix,
            ctaText: "Upgrade",
            expirationCtaText: "Continue with Basic",
            ctaType: "upgrade",
            description: getTaglineForWorkspacePlan("basic"),
            ctaDisabled: false,
            features: {
                dataPrivacy: "Private to your workspace",
                monthlyCredits: getPlan(Plan.UbpBasic).ubp.includedCredits,
                pricePerCredit: getPricePerCreditString(Plan.UbpBasic),
                userSeats: getPlan(Plan.UbpBasic).limit.collaborators,
                projects: getPlan(Plan.UbpBasic).limit.projects,
                roleBasedAccess: getPlan(Plan.UbpBasic).features.rbac,
                customRoles: false,
                sso: false,
                scopedApiKeys: false,
                datasetSizeLimit: "Uncapped; Pay as you go",
                imageAugmentations: `${getPlan(Plan.UbpBasic).limit.variantsPerImage}x`,
                videoProcessing: true,
                datasetSearch: true,
                datasetAnalytics: true,
                enhancedPreprocessing: true,
                enhancedAugmentations: true,
                aiAssistedLabeling: true,
                reviewMode: false,
                annotationHistory: false,
                labelingServices: false,
                annotationInsights: false,
                hostedTraining: "All Models",
                modelWeightsUpload: true,
                downloadWeights: true,
                modelEvaluation: true,
                concurrentTraining: true,
                premiumGpuAccess: "",
                fineTunedModels: true,
                foundationModels: true,
                activeLearning: true,
                buildDeployWorkflows: true,
                workflowVersions: false,
                modelMonitoring: false,
                deviceManagement: false,
                inferenceLicense: true,
                serverlessApi: true,
                dedicatedDeployment: true,
                managedCluster: false,
                inferenceDeploy: true,
                videoStreamMgmt: true,
                inferenceModelLicense: true,
                downloadLicense: "",
                enterpriseNetworking: false,
                offlineDeployment: false,
                kubernetesOperator: false,
                soc2: true,
                communityForum: true,
                emailSupport: "Billing Only",
                liveChat: false,
                onboardingCall: false,
                documentationAiChat: true,
                dedicatedSupport: false,
                onsiteInstallation: false,
                managedServices: false,
                customContract: false,
                activeDirectoryIntegration: false,
                securityReview: false,
                baa: false,
                sla: false
            },
            highlightedFeatures: [
                { text: "Everything in Public Plan, plus:", excludeIcon: true },
                {
                    text: `credits per ${
                        frequency?.interval === "annually" ? "year, loaded upfront" : "month"
                    }`,
                    boldText:
                        frequency?.interval === "annually"
                            ? getPlan(Plan.UbpBasic).ubp.includedCredits * 12
                            : getPlan(Plan.UbpBasic).ubp.includedCredits
                },
                { text: "Team Members", boldText: getPlan(Plan.UbpBasic).limit.collaborators },
                {
                    text: `Commercial License for <a href="https://roboflow.com/inference" target="_blank" class="text-brand font-normal">Self-HostedInference</a>`
                },
                { text: "Model Evaluation" },
                { text: "Model Weights Download", highlight: ["download-weights"] },
                { text: "Additional credits billed per credit" },
                { text: "Billing Support", iconType: "support" }
            ]
        },
        "core": {
            icon: getIconForWorkspacePlan("core"),
            name: getPlan(Plan.UbpCore).meta.title,
            recommended: true,
            recommendedText: "MOST POPULAR",
            price: `$${planPricing.core[frequency?.interval ?? "monthly"].price}`,
            priceDetails: planPricing.core[frequency?.interval ?? "monthly"].priceSuffix,
            condensedPriceDetails:
                planPricing.core[frequency?.interval ?? "monthly"].condensedPriceSuffix,
            ctaText: "Upgrade",
            expirationCtaText: "Continue with Core",
            ctaType: "upgrade",
            description: getTaglineForWorkspacePlan("core"),
            ctaDisabled: false,
            features: {
                dataPrivacy: "Private to your workspace",
                monthlyCredits: getPlan(Plan.UbpCore).ubp.includedCredits,
                pricePerCredit: getPricePerCreditString(Plan.UbpCore),
                userSeats: getPlan(Plan.UbpCore).limit.collaborators,
                projects: getPlan(Plan.UbpCore).limit.projects,
                roleBasedAccess: getPlan(Plan.UbpCore).features.rbac,
                customRoles: false,
                sso: false,
                scopedApiKeys: false,
                datasetSizeLimit: "Uncapped; Pay as you go",
                imageAugmentations: `${getPlan(Plan.UbpCore).limit.variantsPerImage}x`,
                videoProcessing: true,
                datasetSearch: true,
                datasetAnalytics: true,
                enhancedPreprocessing: true,
                enhancedAugmentations: true,
                aiAssistedLabeling: true,
                reviewMode: false,
                annotationHistory: false,
                labelingServices: false,
                annotationInsights: false,
                hostedTraining: "All Models",
                modelWeightsUpload: true,
                downloadWeights: true,
                modelEvaluation: true,
                concurrentTraining: true,
                premiumGpuAccess: "",
                fineTunedModels: true,
                foundationModels: true,
                activeLearning: true,
                buildDeployWorkflows: true,
                workflowVersions: false,
                modelMonitoring: false,
                deviceManagement: false,
                inferenceLicense: true,
                serverlessApi: true,
                dedicatedDeployment: true,
                managedCluster: false,
                inferenceDeploy: true,
                videoStreamMgmt: true,
                inferenceModelLicense: true,
                downloadLicense: "",
                enterpriseNetworking: false,
                offlineDeployment: false,
                kubernetesOperator: false,
                soc2: true,
                communityForum: true,
                emailSupport: "Billing Only",
                liveChat: false,
                onboardingCall: false,
                documentationAiChat: true,
                dedicatedSupport: false,
                onsiteInstallation: false,
                managedServices: false,
                customContract: false,
                activeDirectoryIntegration: false,
                securityReview: false,
                baa: false,
                sla: false
            },
            highlightedFeatures: [
                { text: "Everything in Public Plan, plus:", excludeIcon: true },
                {
                    text: `credits per ${
                        frequency?.interval === "annually" ? "year, loaded upfront" : "month"
                    }`,
                    boldText:
                        frequency?.interval === "annually"
                            ? getPlan(Plan.UbpCore).ubp.includedCredits * 12
                            : getPlan(Plan.UbpCore).ubp.includedCredits
                },
                { text: "Team Members", boldText: getPlan(Plan.UbpCore).limit.collaborators },
                {
                    text: `Commercial License for <a href="https://roboflow.com/inference" target="_blank" class="text-brand font-normal">Self-Hosted Inference</a>`
                },
                { text: "Model Evaluation" },
                { text: "Model Weights Download", highlight: ["download-weights"] },
                { text: "Additional credits billed per credit" },
                { text: "Community Support", iconType: "support" }
            ]
        },
        "growth-ubp": {
            icon: getIconForWorkspacePlan("growth-ubp"),
            name: "Growth",
            recommended: false,
            price: `$${planPricing["growth-ubp"][frequency?.interval ?? "monthly"].price}`,
            priceDetails: planPricing["growth-ubp"][frequency?.interval ?? "monthly"].priceSuffix,
            condensedPriceDetails:
                planPricing["growth-ubp"][frequency?.interval ?? "monthly"].condensedPriceSuffix,
            ctaText: "Upgrade",
            expirationCtaText: "Continue with Growth",
            ctaType: "upgrade",
            ctaDisabled: false,
            description: getTaglineForWorkspacePlan("growth-ubp"),
            features: {
                dataPrivacy: "Private to your workspace",
                monthlyCredits: getPlan(Plan.UbpGrowth).ubp.includedCredits,
                pricePerCredit: getPricePerCreditString(Plan.UbpGrowth),
                userSeats: getPlan(Plan.UbpGrowth).limit.collaborators,
                projects: getPlan(Plan.UbpGrowth).limit.projects,
                roleBasedAccess: getPlan(Plan.UbpGrowth).features.rbac,
                customRoles: false,
                sso: false,
                scopedApiKeys: false,
                datasetSizeLimit: "Uncapped; Pay as you go",
                imageAugmentations: `${getPlan(Plan.UbpGrowth).limit.variantsPerImage}x`,
                videoProcessing: true,
                datasetSearch: true,
                datasetAnalytics: true,
                enhancedPreprocessing: true,
                enhancedAugmentations: true,
                aiAssistedLabeling: true,
                reviewMode: true,
                annotationHistory: true,
                labelingServices: "Annual Plans Only",
                annotationInsights: false,
                hostedTraining: "All Models",
                modelWeightsUpload: true,
                downloadWeights: true,
                modelEvaluation: true,
                concurrentTraining: true,
                premiumGpuAccess: "When Available",
                fineTunedModels: true,
                foundationModels: true,
                activeLearning: true,
                buildDeployWorkflows: true,
                workflowVersions: true,
                modelMonitoring: "7 days of data",
                deviceManagement: false,
                inferenceLicense: true,
                serverlessApi: true,
                dedicatedDeployment: true,
                managedCluster: false,
                inferenceDeploy: true,
                videoStreamMgmt: true,
                inferenceModelLicense: true,
                downloadLicense: "10 devices included",
                enterpriseNetworking: false,
                offlineDeployment: false,
                kubernetesOperator: false,
                soc2: true,
                communityForum: true,
                emailSupport: true,
                liveChat: true,
                onboardingCall: true,
                documentationAiChat: true,
                dedicatedSupport: false,
                onsiteInstallation: false,
                managedServices: false,
                customContract: false,
                activeDirectoryIntegration: false,
                securityReview: false,
                baa: false,
                sla: false
            },
            highlightedFeatures: [
                { text: "Everything in Basic Plan, plus:", excludeIcon: true },
                {
                    text: `credits per ${
                        frequency?.interval === "annually" ? "year, loaded upfront" : "month"
                    }`,
                    boldText:
                        frequency?.interval === "annually"
                            ? getPlan(Plan.UbpGrowth).ubp.includedCredits * 12
                            : getPlan(Plan.UbpGrowth).ubp.includedCredits
                },
                { boldText: getPlan(Plan.UbpGrowth).limit.collaborators, text: "Team Members" },
                { text: "Role-Based Access Control", highlight: ["role-based-access-control"] },
                { text: "Model Monitoring" },
                { text: "Access to Professional Labeling Services (annual only)" },
                { text: "Dedicated Onboarding" },
                { text: "Additional credits billed per credit" },
                { text: "Priority Chat and Email Support", iconType: "support" }
            ]
        },
        "enterprise": {
            icon: getIconForWorkspacePlan("enterprise"),
            name: "Enterprise",
            price: "Custom Pricing",
            priceDetails: "Billed annually",
            ctaText: "Upgrade",
            ctaType: "secondary",
            description: getTaglineForWorkspacePlan("enterprise"),
            ctaDisabled: false,
            features: {
                dataPrivacy: "Private to your workspace",
                monthlyCredits: "Custom",
                pricePerCredit: "Custom",
                userSeats: "Custom",
                projects: "Custom",
                roleBasedAccess: true,
                customRoles: true,
                sso: true,
                scopedApiKeys: true,
                datasetSizeLimit: "Uncapped; Pay as you go",
                imageAugmentations: "Custom",
                videoProcessing: true,
                datasetSearch: true,
                datasetAnalytics: true,
                enhancedPreprocessing: true,
                enhancedAugmentations: true,
                aiAssistedLabeling: true,
                reviewMode: true,
                annotationHistory: true,
                labelingServices: true,
                annotationInsights: true,
                hostedTraining: "All Models",
                modelWeightsUpload: true,
                downloadWeights: "Optional",
                modelEvaluation: true,
                concurrentTraining: true,
                premiumGpuAccess: "Priority Access",
                fineTunedModels: true,
                foundationModels: true,
                activeLearning: true,
                buildDeployWorkflows: true,
                workflowVersions: true,
                modelMonitoring: "Custom",
                deviceManagement: true,
                inferenceLicense: true,
                serverlessApi: true,
                dedicatedDeployment: true,
                managedCluster: true,
                inferenceDeploy: true,
                videoStreamMgmt: true,
                inferenceModelLicense: true,
                downloadLicense: "Optional",
                enterpriseNetworking: true,
                offlineDeployment: true,
                kubernetesOperator: true,
                soc2: true,
                communityForum: true,
                emailSupport: true,
                liveChat: true,
                onboardingCall: true,
                documentationAiChat: true,
                dedicatedSupport: true,
                onsiteInstallation: true,
                managedServices: true,
                customContract: true,
                activeDirectoryIntegration: true,
                securityReview: true,
                baa: true,
                sla: true
            },
            highlightedFeatures: [
                { text: "Everything in Basic Plan, plus:", excludeIcon: true },
                { boldText: "Custom limits" },
                { text: "Single Sign-On (SSO)" },
                { text: "Custom Solution Design" },
                { text: "Device Management" },
                { text: "Hardware Procurement" },
                { text: "Scoped API Keys" },
                { text: "Custom SLAs" },
                { text: "Priority Access to Premium GPUs" },
                { text: "HIPAA Compliance + BAA Execution" },
                { text: "Dedicated Field Engineering Support", iconType: "support" }
            ]
        }
    };

    // Remove delisted plans for users not currently on them
    // Only apply delisting if includedPlans is not explicitly provided
    if (!includedPlans) {
        const visiblePlans = getPricingTablePlans(currentPlan);
        const visiblePlanKeys = visiblePlans.map((plan) => String(plan));
        Object.keys(planData).forEach((planKey) => {
            if (!visiblePlanKeys.includes(planKey)) {
                delete planData[planKey];
            }
        });
    }

    if (includedPlans) {
        planData = Object.fromEntries(
            Object.entries(planData).filter(([key]) => includedPlans.includes(key))
        );
    }
    return planData;
}

export function getFeatureGroups() {
    return [
        {
            section: "Access",
            features: [
                { name: "Data and Model Privacy", type: "custom", key: "dataPrivacy" },
                {
                    name: "Monthly Included Credits",
                    type: "custom",
                    key: "monthlyCredits",
                    explainerText:
                        "Credits are consumed for image storage, model training, AI labeling, and model deployment.",
                    link: "https://roboflow.com/credits"
                },
                {
                    name: "Flex Credit Cost",
                    type: "custom",
                    key: "pricePerCredit",
                    explainerText:
                        "Flex credits are credits consumed above your Included amount. These are billed on a monthly cycle."
                },
                { name: "Team Members", type: "custom", key: "userSeats" },
                { name: "Projects", type: "custom", key: "projects" },
                {
                    name: "Role-Based Access Control",
                    type: "checkmark",
                    key: "roleBasedAccess",
                    explainerText:
                        "With RBAC, you can control actions that users in your workspace can perform from Labelers, Reviewers to Admins."
                },
                {
                    name: "Custom Roles",
                    type: "checkmark",
                    key: "customRoles",
                    explainerText: "Create custom roles and permissions in your workspace."
                },
                {
                    name: "Project Folders",
                    type: "checkmark",
                    key: "projectFolders",
                    explainerText:
                        "Organize your projects into folders and assign access permissions."
                },
                { name: "SSO", type: "checkmark", key: "sso" },
                { name: "Scoped API Keys", type: "checkmark", key: "scopedApiKeys" }
            ]
        },
        {
            section: "Datasets",
            features: [
                { name: "Workspace Dataset Size Limit", type: "custom", key: "datasetSizeLimit" },
                {
                    name: "Image Augmentations",
                    type: "custom",
                    key: "imageAugmentations",
                    explainerText:
                        "The maximum number of augmented/processed images you can generate per original image."
                },
                { name: "Video Processing", type: "checkmark", key: "videoProcessing" },
                { name: "Dataset Search", type: "checkmark", key: "datasetSearch" },
                { name: "Dataset Analytics", type: "checkmark", key: "datasetAnalytics" },
                { name: "Enhanced Preprocessing", type: "checkmark", key: "enhancedPreprocessing" },
                { name: "Enhanced Augmentations", type: "checkmark", key: "enhancedAugmentations" }
            ]
        },
        {
            section: "Labeling",
            features: [
                {
                    name: "AI Labeling",
                    type: "checkmark",
                    key: "aiAssistedLabeling",
                    explainerText:
                        "Access to features that speed up the labeling process with AI, like Label Assist, Smart Select, Box Prompting, and Auto Label. Uses credits."
                },
                {
                    name: "Review Mode",
                    type: "checkmark",
                    key: "reviewMode",
                    explainerText:
                        "Require images to be reviewed before being adding to your dataset."
                },
                {
                    name: "Labeling History",
                    type: "checkmark",
                    key: "annotationHistory",
                    explainerText: "See the history of every annotation made on an image."
                },
                {
                    name: "Outsourced Labeling Services",
                    type: "custom",
                    key: "labelingServices",
                    explainerText:
                        "Instead of manually labeling, have Roboflow's team of labelers label your data."
                },
                {
                    name: "Labeling Insights",
                    type: "checkmark",
                    key: "annotationInsights",
                    explainerText:
                        "Get insights into your annotation process: How many images were labeled and by whom."
                }
            ]
        },
        {
            section: "Models",
            features: [
                { name: "Hosted Training", type: "custom", key: "hostedTraining" },
                { name: "Model Weights Upload", type: "checkmark", key: "modelWeightsUpload" },
                {
                    name: "Download Model Weights",
                    type: "custom",
                    key: "downloadWeights",
                    explainerText:
                        "Some embedded devices do not support Roboflow Inference. For those you can download the model weights for manual deployment."
                },
                { name: "Model Evaluation", type: "checkmark", key: "modelEvaluation" },
                { name: "Concurrent Training Jobs", type: "checkmark", key: "concurrentTraining" },
                { name: "Premium GPU Access", type: "custom", key: "premiumGpuAccess" }
            ]
        },
        {
            section: "Deployments",
            features: [
                { name: "Fine-Tuned Models", type: "checkmark", key: "fineTunedModels" },
                { name: "Foundation Models", type: "checkmark", key: "foundationModels" },
                { name: "Active Learning", type: "checkmark", key: "activeLearning" },
                {
                    name: "Build and Deploy Workflows",
                    type: "checkmark",
                    key: "buildDeployWorkflows"
                },
                {
                    name: "Workflow Versions",
                    type: "checkmark",
                    key: "workflowVersions",
                    explainerText: "Save and restore historical versions of your workflows."
                },
                {
                    name: "Model Monitoring",
                    type: "custom",
                    key: "modelMonitoring",
                    explainerText: "Gain insights into your model's real time performance."
                },
                { name: "Device Management", type: "checkmark", key: "deviceManagement" },
                { subsection: "Roboflow-Hosted" },
                {
                    name: "Commercial Model License",
                    type: "checkmark",
                    key: "inferenceLicense",
                    explainerText: "All Roboflow-hosted models come with a commercial license.",
                    link: "https://roboflow.com/licensing"
                },
                { name: "Serverless Hosted API", type: "checkmark", key: "serverlessApi" },
                { name: "Dedicated Deployment", type: "checkmark", key: "dedicatedDeployment" },
                { name: "Managed CPU / GPU Cluster", type: "checkmark", key: "managedCluster" },
                { subsection: "Self-Hosted" },
                { name: "Self-Host with Inference", type: "checkmark", key: "inferenceDeploy" },
                { name: "Video Stream Management", type: "checkmark", key: "videoStreamMgmt" },
                {
                    name: "Inference Model License",
                    type: "custom",
                    key: "inferenceModelLicense",
                    explainerText:
                        "Some models need a special license to be used commercially. All paid plans include a commercial license to use and deploy models with Roboflow Inference."
                },
                {
                    name: "Download Model License",
                    type: "custom",
                    key: "downloadLicense",
                    explainerText:
                        "Some models need a special license to be used commercially. Enterprise plans also include a commercial license to use these models outside of the Roboflow ecosystem."
                },
                { name: "Enterprise Networking", type: "checkmark", key: "enterpriseNetworking" },
                { name: "Offline Deployment", type: "checkmark", key: "offlineDeployment" },
                { name: "Kubernetes Support", type: "checkmark", key: "kubernetesOperator" }
            ]
        },
        {
            section: "Security & Support",
            features: [
                { name: "SOC2", type: "checkmark", key: "soc2" },
                { name: "Community Forum", type: "checkmark", key: "communityForum" },
                { name: "Documentation + AI Chat", type: "checkmark", key: "documentationAiChat" },
                { name: "Email Support", type: "custom", key: "emailSupport" },
                { name: "Live Chat", type: "checkmark", key: "liveChat" },
                { name: "Onboarding Call", type: "checkmark", key: "onboardingCall" },
                { name: "Dedicated Support", type: "checkmark", key: "dedicatedSupport" },
                { name: "Onsite Installation", type: "checkmark", key: "onsiteInstallation" },
                { name: "Managed Services", type: "checkmark", key: "managedServices" },
                { name: "Custom Contract", type: "checkmark", key: "customContract" },
                {
                    name: "Active Directory Integration",
                    type: "checkmark",
                    key: "activeDirectoryIntegration"
                },
                { name: "Security Review", type: "checkmark", key: "securityReview" },
                { name: "BAA", type: "checkmark", key: "baa" },
                { name: "SLA", type: "checkmark", key: "sla" }
            ]
        }
    ];
}

export default { getPlanData, getFeatureGroups };
